import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, fmtDate } from "../../lib/api";
import { useAuth } from "../../lib/auth-context";
import { useI18n } from "../../lib/i18n";
import MobileHeader from "../../components/MobileHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import GroupScoreCard from "../../components/GroupScoreCard";
import {
    Users, ChevronRight, CheckCircle2, Clock, XCircle, ShoppingBag,
    Plus, AlertTriangle, Truck,
} from "lucide-react";

const STATUS_KEYS = {
    pending_co_signers: { key: "splitpay.share_status.pending_acceptance", fallback: "Awaiting co-signers", icon: Clock, color: "bg-amber-50 text-amber-700 border-amber-200" },
    active:             { key: "splitpay.share_status.active",             fallback: "Active",              icon: ShoppingBag, color: "bg-blue-50 text-blue-700 border-blue-200" },
    repaid:             { key: "splitpay.share_status.repaid",             fallback: "Repaid",              icon: CheckCircle2, color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    cancelled:          { key: "splitpay.share_status.cancelled",          fallback: "Cancelled",           icon: XCircle, color: "bg-slate-50 text-slate-600 border-slate-200" },
};

export default function SplitPayList() {
    const [loans, setLoans] = useState(null);
    const { user } = useAuth();
    const { t } = useI18n();
    const isBusiness = user?.role === "business";
    const baseRoute = isBusiness ? "/business" : "/consumer";
    const creditRoute = isBusiness ? "/business/loans" : "/consumer/credit";

    useEffect(() => {
        api.get("/splitpay").then((r) => setLoans(r.data));
    }, []);

    const pendingInvites = (loans || []).filter((L) => L.status === "pending_co_signers" && L.my_role !== "initiator" && L.my_role !== "supplier" && !L.my_accepted);
    const active = (loans || []).filter((L) => L.status === "active");
    const others = (loans || []).filter((L) => !pendingInvites.includes(L) && !active.includes(L));

    return (
        <div data-testid="splitpay-list-page">
            <MobileHeader title={t("splitpay.title")} subtitle={isBusiness ? t("splitpay.list.subtitle_business") : t("splitpay.list.subtitle_consumer")} backTo={creditRoute} variant="purple" />
            <div className="px-5 -mt-6 pb-6 space-y-4">
                <Link to={`${baseRoute}/credit/SplitPay`} data-testid="splitpay-new-btn">
                    <Button className="w-full h-12 rounded-xl bg-[#7000FF] hover:bg-[#5d00d6] font-semibold">
                        <Plus size={16} /> {isBusiness ? t("splitpay.list.new_business") : t("splitpay.list.new")}
                    </Button>
                </Link>

                {loans === null && <div className="text-center py-12 text-slate-400">Loading…</div>}

                {pendingInvites.length > 0 && (
                    <Section title={t("splitpay.list.section.invites")} testid="splitpay-section-invites">
                        {pendingInvites.map((L) => <LoanCard key={L.id} L={L} highlight baseRoute={baseRoute} t={t} />)}
                    </Section>
                )}

                {active.length > 0 && (
                    <Section title={t("splitpay.list.section.active")} testid="splitpay-section-active">
                        {active.map((L) => <LoanCard key={L.id} L={L} baseRoute={baseRoute} t={t} />)}
                    </Section>
                )}

                {others.length > 0 && (
                    <Section title={t("splitpay.list.section.past")} testid="splitpay-section-past">
                        {others.map((L) => <LoanCard key={L.id} L={L} baseRoute={baseRoute} t={t} />)}
                    </Section>
                )}

                {loans !== null && loans.length === 0 && (
                    <div className="text-center py-12">
                        <div className="size-16 mx-auto rounded-2xl bg-purple-50 flex items-center justify-center text-[#7000FF]">
                            <Users size={28} />
                        </div>
                        <div className="font-display text-lg font-semibold mt-4 text-[#0A1930]">{t("splitpay.list.empty_title")}</div>
                        <div className="text-sm text-slate-500 mt-1 max-w-xs mx-auto">
                            {isBusiness ? t("splitpay.list.empty_business") : t("splitpay.list.empty_consumer")}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function Section({ title, testid, children }) {
    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 px-1">{title}</div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function LoanCard({ L, highlight, baseRoute, t }) {
    const metaConf = STATUS_KEYS[L.status] || STATUS_KEYS.active;
    const baseLabel = L.status === "pending_co_signers" ? "Awaiting co-signers" : (t ? t(metaConf.key) : metaConf.fallback);
    const meta = { label: baseLabel, icon: metaConf.icon, color: metaConf.color };
    const Icon = meta.icon;
    const isOverdue = L.my_share_status === "overdue";
    const isSupplierBulk = L.loan_type === "supplier_bulk";
    const isSupplier = L.my_role === "supplier";
    return (
        <Link to={`${baseRoute || "/consumer"}/splitpay/${L.id}`} data-testid={`splitpay-card-${L.id}`}>
            <div className={`bg-white rounded-2xl p-4 border product-shadow tap-feedback ${
                highlight ? "border-amber-300 bg-amber-50/30" : "border-slate-200/60"
            }`}>
                <div className="flex items-center justify-between mb-2 gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge className={`${meta.color} text-[10px] font-bold border`}>
                            <Icon size={10} className="mr-1" />{meta.label}
                        </Badge>
                        {isSupplierBulk && (
                            <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] font-bold border">
                                <Truck size={10} className="mr-1" /> Bulk
                            </Badge>
                        )}
                        {L.group_score && (
                            <GroupScoreCard score={L.group_score} grade={L.group_grade} compact />
                        )}
                    </div>
                    {isOverdue && (
                        <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-[10px] font-bold border flex-shrink-0">
                            <AlertTriangle size={10} className="mr-1" /> Overdue
                        </Badge>
                    )}
                </div>
                <div className="flex items-center justify-between">
                    <div className="min-w-0">
                        <div className="font-display text-lg font-bold text-[#0A1930] truncate">{fmtCurrency(L.amount)}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                            {L.member_count} members · {L.initiator_name} · {fmtDate(L.created_at)}
                        </div>
                        {isSupplier ? (
                            <div className="text-[11px] text-purple-700 font-semibold mt-1">
                                Receivable: {fmtCurrency(L.amount + (L.interest || 0) + (L.fees || 0))}
                            </div>
                        ) : (
                            <div className="text-[11px] text-purple-700 font-semibold mt-1">
                                My share: {fmtCurrency(L.my_share_total)} {L.my_share_repaid > 0 && `(${fmtCurrency(L.my_share_repaid)} paid)`}
                            </div>
                        )}
                    </div>
                    <ChevronRight size={18} className="text-slate-300 flex-shrink-0 ml-2" />
                </div>
            </div>
        </Link>
    );
}
